/**
 * Created by Ning Jiang on 2/7/2018.
 */

import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {GameMode} from "@nolimitcity/slot-game/bin/core/gamemode/GameMode";
import {GameSetting} from "@nolimitcity/slot-game/bin/core/setting/GameSetting";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {StepCountUpBigWinController} from "@nolimitcity/slot-game/bin/game/winpresentation/bigwin/stepcountup/StepCountUpBigWinController";
import {Timeline, TimelineLite, TweenLite} from "gsap";
import {TombOfNefertitiMultiplierEvents} from "../../multiplierIndicator/TombOfNefertitiMultiplier";
import {TombOfNefertitiParsedGameData} from "../../server/data/TombOfNefertitiParsedGameData";
import {TombOfNefertitiSpinEvent} from "../../spin/TombOfNefertitiSpinController";
import {TombOfNefertitiGameConfig} from "../../TombOfNefertitiGameConfig";
import {TombOfNefertitiSoundConfig} from "../../TombOfNefertitiSoundConfig";
import {TombOfNefertitiInitialWPView} from "../initial/TombOfNefertitiInitialWPView";
import {TombOfNefertitiBigWinCountUp} from "./TombOfNefertitiBigWinCountUp";
import {TombOfNefertitiBigWinView} from "./TombOfNefertitiBigWinView";

export class TombOfNefertitiBigWinController extends StepCountUpBigWinController {

    private _gameView:TombOfNefertitiBigWinView;
    private _winCountUp:TombOfNefertitiBigWinCountUp;
    private _nextGameMode:string;

    private _totalReSpinView:TombOfNefertitiInitialWPView;

    private _bigWinCountUpTl:Timeline | null;

    private _isMultiplierShown:boolean = false;
    private _canShowReSpinWinAmount:boolean = false;
    private _isReSpinAmountItemShown:boolean = false;
    private _reSpinWinAmount:number = 0;

    private _abortTl:Timeline | null;
    private _abortCountUpTl:Timeline | null;

    private _allSoundsStopped:boolean = false;

    constructor(indexes:[number, number]) {
        super(indexes, {
            skipLastLevelCountUpIfSmall : false
        });

        this._gameView = new TombOfNefertitiBigWinView();
        this._winCountUp = new TombOfNefertitiBigWinCountUp();
        this._winCountUp.position.set(GameConfig.instance.GAME_WIDTH * 0.5, GameConfig.instance.GAME_HEIGHT * 0.55);
        StageManager.getLayer(TombOfNefertitiGameConfig.instance.LAYERS.WIN_COUNT_UP.name).addChild(this._winCountUp);

        this._totalReSpinView = new TombOfNefertitiInitialWPView();
    }

    protected parseFeatureGameData(data:TombOfNefertitiParsedGameData):void {
        this._nextGameMode = data.nextMode;
        this._isMultiplierShown = false;

        this._isReSpinAmountItemShown = false;

        this._canShowReSpinWinAmount = (data.totalRespinWin > 0 && (data.nextMode === GameMode.NORMAL || data.nextMode === GameMode.FREESPIN));
        if(this._canShowReSpinWinAmount) {
            this._reSpinWinAmount = data.totalRespinWin;
        }

        super.parseFeatureGameData(data);
    }

    protected getBigWinLevelAnimation(level:number):Timeline {
        this._isMultiplierShown = false;
        this._isReSpinAmountItemShown = false;
        return this._gameView.getBigWinLevelAnimation(this._winRatios[level], level);
    }

    protected playBigWinLevelSound(level:number):void {
        if(level === 0) {
            SlotGame.sound.fadeAmbience(0, 500);
        }
        SlotGame.sound.playEffect(TombOfNefertitiSoundConfig.instance.BIG_WIN[level]);
    }

    protected playBigWinCountUpSound(level:number):void {
        if(level === 0) {
            // #395
            this._allSoundsStopped = false;

            TweenLite.delayedCall(0.3, () => {
                if(!this._allSoundsStopped) {
                    SlotGame.sound.playEffect(TombOfNefertitiSoundConfig.instance.COUNT_UP, -1);
                }
            });
        }
    }

    protected playCountUp(from:number, to:number, duration:number, preTotalWin:number = 0):Timeline | null {
        const retVal:TimelineLite | null = (this._winCountUp.getCountUpAnimation(from, to, duration)) as TimelineLite;

        // for each big win level we get call in this function but we want to run only once and at the end
        if(to === this._gameData!.singleWin) {
            retVal.add(() => SlotGame.sound.stopEffect(TombOfNefertitiSoundConfig.instance.COUNT_UP));
            retVal.add(this._gameView.playEndingAnimation(this._winLevel), retVal.totalDuration());
            retVal.add(() => SlotGame.winFieldController.showWinField(this._gameData!.totalWin));
            retVal.add(this._winCountUp.abort(this._gameData!.singleWin, false), retVal.totalDuration() - 0.25);

            if(this._canShowReSpinWinAmount) {
                retVal.add(() => this.showTotalWin(), retVal.totalDuration());
                retVal.add(TweenLite.to(() => {}, 0.4, {}));
            }
            //adding blank animation for pause purpose in the presentation so that total respin win amount is shown.
            let currentModeFreeSpin:boolean = (this._gameData!.mode.indexOf(GameMode.FREESPIN) !== -1),
                isAutoplayActive = GameSetting.isAutoplayRound;

            if(currentModeFreeSpin || (!currentModeFreeSpin && ((this._nextGameMode === GameMode.FREESPIN)))) {
                retVal.add(TweenLite.to(() => {}, 1, {}));
                if(!isAutoplayActive) {
                    retVal.add(() => EventHandler.dispatchEvent(new GameEvent("FORCE_HIDE_TOTAL_WIN")));
                }
            }

            if(this._gameData!.mode.indexOf(GameMode.FREESPIN) !== -1 && this._gameData!.nextMode !== GameMode.NORMAL) {
                retVal.add(() => EventHandler.dispatchEvent(new GameEvent(TombOfNefertitiMultiplierEvents.TRY_MULTIPLIER_UPDATE)));
                retVal.add(TweenLite.to(() => {}, 2, {}));
            }
        }

        this._bigWinCountUpTl = retVal;

        return retVal;
    }

    private showTotalWin():void {
        EventHandler.dispatchEvent(new GameEvent("UPDATE_INIT_COUNTUP", (this._gameData! as TombOfNefertitiParsedGameData).totalRespinWin));

        let currentModeFreeSpin:boolean = (this._gameData!.mode.indexOf(GameMode.FREESPIN) !== -1),
            isAutoplayActive = GameSetting.isAutoplayRound;

        if(currentModeFreeSpin || (!currentModeFreeSpin && (isAutoplayActive || (this._nextGameMode === GameMode.FREESPIN)))) {
            TweenLite.delayedCall(1.2, () => {
                EventHandler.dispatchEvent(new GameEvent("FORCE_HIDE_TOTAL_WIN"));
            });
        }

        this._isReSpinAmountItemShown = true;
    }

    private onAbortAddDelay():void {
        const isAutoplayActive:boolean = GameSetting.isAutoplayRound;

        if((!isAutoplayActive && (this._gameData!.nextMode === GameMode.NORMAL))) {
            if(!TombOfNefertitiGameConfig.instance.SPIN_DELAY) {
                EventHandler.dispatchEvent(new GameEvent(TombOfNefertitiSpinEvent.ADD_DELAY_IN_START_SPIN, 0.8));
            }
        } else {
            EventHandler.dispatchEvent(new GameEvent(TombOfNefertitiSpinEvent.ADD_DELAY_IN_START_SPIN, 0.8));
        }
    }

    protected playEndingAnimation(isAborted:boolean):void {
        this._abortTl = null;
        this._abortCountUpTl = null;

        if(isAborted) {
            if(this._isMultiplierShown) {
                return;
            } else {
                EventHandler.dispatchEvent(new GameEvent(TombOfNefertitiMultiplierEvents.TRY_MULTIPLIER_UPDATE, isAborted));
            }

            if(this._canShowReSpinWinAmount) {
                if(!this._isReSpinAmountItemShown) {
                    this.showTotalWin();
                }
                this.onAbortAddDelay();
            }

            if(this._bigWinCountUpTl) {
                this._bigWinCountUpTl!.pause();
                this._bigWinCountUpTl!.kill();
                this._bigWinCountUpTl = null;
            }

            this._abortTl = this._gameView.playEndingAnimation(this._winLevel);
            SlotGame.winFieldController.showWinField(this._gameData!.totalWin);
            this._abortCountUpTl = this._winCountUp.abort(this._gameData!.singleWin, isAborted);
        }

        if(!this._canShowReSpinWinAmount) {
            EventHandler.dispatchEvent(new GameEvent("UPDATE_INIT_COUNTUP", (this._gameData! as TombOfNefertitiParsedGameData).totalRespinWin));
        }
    }

    protected addFeatureEventHandlers():void {
        super.addFeatureEventHandlers && super.addFeatureEventHandlers();

        EventHandler.addEventListener(this, TombOfNefertitiMultiplierEvents.MULTIPLIER_SHOWN, () => this._isMultiplierShown = true);
    }

    protected stopAllSounds():void {
        for(let i:number = 0; i < TombOfNefertitiSoundConfig.instance.BIG_WIN.length; ++i) {
            SlotGame.sound.stopEffect(TombOfNefertitiSoundConfig.instance.BIG_WIN[i], 200);
        }

        SlotGame.sound.stopEffect(TombOfNefertitiSoundConfig.instance.COUNT_UP);
        this._allSoundsStopped = true;

        SlotGame.sound.fadeAmbience(1, 1000);
    }
}